import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, MapPin, Phone, Star, Clock, Award, Zap, Camera, Bell, Home, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import DOMPurify from 'dompurify';

const AlarmeHeraultPage = () => {
  const cities = [
    'Montpellier', 'Béziers', 'Sète', 'Lunel', 'Agde', 'Frontignan', 
    'Mauguio', 'Lattes', 'Castelnau-le-Lez', 'Pérols', 'Saint-Jean-de-Védas',
    'Palavas-les-Flots', 'La Grande-Motte', 'Carnon', 'Villeneuve-lès-Maguelone',
    'Grabels', 'Juvignac', 'Clapiers', 'Jacou', 'Le Crès', 'Vendargues',
    'Castries', 'Mèze', 'Marseillan', 'Pézenas', 'Lodève', 'Clermont-l\'Hérault'
  ];

  const advantages = [
    { icon: Shield, title: "Expert Ajax Systems N°1", desc: "Installateur certifié et distributeur officiel Ajax Systems dans l'Hérault" },
    { icon: Clock, title: "Intervention rapide", desc: "Installation sous 48h dans tout le département 34" },
    { icon: Award, title: "Garantie 4 ans", desc: "Tous nos produits Ajax sont garantis 4 ans pièces et main d'œuvre" },
    { icon: Zap, title: "Autonomie exceptionnelle", desc: "Jusqu'à 7 ans d'autonomie sur batterie pour les détecteurs" },
  ];

  const services = [
    { icon: Bell, title: "Alarme sans fil Ajax", desc: "Systèmes d'alarme Grade 2 et Grade 3 pour particuliers et professionnels" },
    { icon: Camera, title: "Vidéosurveillance IA", desc: "Caméras avec détection intelligente humains/véhicules, zéro fausse alerte" },
    { icon: Home, title: "Domotique connectée", desc: "Automatisation éclairage, volets, chauffage via l'écosystème Ajax" },
    { icon: Building, title: "Sécurité entreprise", desc: "Solutions professionnelles pour commerces, bureaux et sites industriels" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "FGS Alarme Montpellier - Alarme Ajax Systems Hérault",
    "description": "Installation alarme et vidéosurveillance Ajax Systems dans l'Hérault (34). Expert certifié, intervention rapide Montpellier et environs.",
    "url": "https://maprotection-ia.fr/alarme-ajax-herault",
    "telephone": "+33745040929",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6 Bis Boulevard Berthelot",
      "addressLocality": "Montpellier",
      "postalCode": "34000",
      "addressRegion": "Hérault",
      "addressCountry": "FR"
    },
    "areaServed": {
      "@type": "State",
      "name": "Hérault"
    },
    "priceRange": "€€",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "89",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  return (
    <>
      <Helmet>
        <title>Alarme Hérault : Installation Ajax Systems Montpellier Béziers Sète (34) | Devis Gratuit | FGS Alarme</title>
        <meta name="description" content="★ Installateur alarme Hérault certifié Ajax Systems. Alarme maison sans fil, vidéosurveillance IA, télésurveillance 24/7. Intervention Montpellier, Béziers, Sète sous 48h. Devis gratuit. ☎ 07 45 04 09 29" />
        <meta name="keywords" content="alarme ajax systems hérault, installation alarme montpellier, vidéosurveillance béziers, caméra sète, alarme sans fil 34, installateur ajax hérault, alarme maison montpellier, sécurité entreprise hérault, prix alarme hérault" />
        <link rel="canonical" href="https://maprotection-ia.fr/alarme-ajax-herault" />
        
        {/* Geo Meta Tags */}
        <meta name="geo.region" content="FR-34" />
        <meta name="geo.placename" content="Montpellier" />
        <meta name="geo.position" content="43.6108;3.8767" />
        <meta name="ICBM" content="43.6108, 3.8767" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Alarme Hérault : Installation Ajax Systems (34) | Devis Gratuit" />
        <meta property="og:description" content="Installateur certifié Ajax Systems dans l'Hérault. Alarme sans fil, vidéosurveillance IA, garantie 4 ans. Devis gratuit sous 24h." />
        <meta property="og:url" content="https://maprotection-ia.fr/alarme-ajax-herault" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fb.png" />
        
        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(structuredData)) }} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://maprotection-ia.fr" },
              { "@type": "ListItem", "position": 2, "name": "Alarme Hérault", "item": "https://maprotection-ia.fr/alarme-ajax-herault" }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black">
        {/* Hero Section */}
        <section className="relative py-16 md:py-20 bg-[#0a0a0a] overflow-hidden">
          <div className="max-w-[1400px] mx-auto px-4 md:px-12 relative z-10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Département de l'Hérault (34)</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-semibold text-center text-white mb-4 tracking-tight">
              Installation <span className="text-emerald-400">Alarme Ajax Systems</span>
              <br />dans l'Hérault
            </h1>
            
            <p className="text-base md:text-lg text-white/60 text-center max-w-3xl mx-auto mb-8 font-light">
              FGS Alarme Montpellier, votre expert n°1 en alarme et vidéosurveillance Ajax Systems 
              à Montpellier et dans tout l'Hérault. Installation professionnelle, garantie 4 ans.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-white text-black hover:bg-white/90 font-medium rounded-full h-12 px-8">
                <a href="tel:+33745040929">
                  <Phone className="w-4 h-4 mr-2" />
                  07 45 04 09 29
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full h-12 px-8">
                <Link to="/contact">Devis gratuit en 24h</Link>
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-12">
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className="text-white font-medium">4.9/5</span>
                <span className="text-white/50">(89 avis)</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Shield className="w-5 h-5 text-emerald-400" />
                <span className="text-white">Certifié Ajax Systems</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Award className="w-5 h-5 text-emerald-400" />
                <span className="text-white">Garantie 4 ans</span>
              </div>
            </div>
          </div>
        </section>

        {/* Avantages */}
        <section className="py-16 bg-black/40">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-12">
              Pourquoi choisir FGS Alarme Montpellier dans l'Hérault ?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((item, index) => (
                <div key={index} className="bg-white/[0.02] p-6 rounded-xl border border-white/5 hover:border-fgs-primary transition-colors">
                  <item.icon className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/50">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 bg-white/[0.02]">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-4">
              Nos services dans l'Hérault
            </h2>
            <p className="text-white/50 text-center max-w-2xl mx-auto mb-12">
              Solutions complètes de sécurité Ajax Systems pour particuliers et professionnels
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {services.map((service, index) => (
                <div key={index} className="flex gap-4 p-6 bg-black/40 rounded-xl border border-white/5">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-white/50">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Zone d'intervention */}
        <section className="py-16 bg-black/40">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-4">
              Zone d'intervention dans l'Hérault
            </h2>
            <p className="text-white/50 text-center max-w-2xl mx-auto mb-12">
              Installation et dépannage alarme Ajax Systems dans toutes les communes du département 34
            </p>
            
            <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
              {cities.map((city, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-white/[0.02] border border-white/5 rounded-full text-sm text-white/50 hover:text-emerald-400 hover:border-fgs-primary transition-colors cursor-default"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-fgs-primary/20 to-fgs-surface">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Protégez votre domicile ou entreprise dans l'Hérault
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto mb-8">
              Obtenez un devis gratuit sous 24h pour l'installation de votre système d'alarme Ajax Systems
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold">
                <a href="tel:+33745040929">
                  <Phone className="w-5 h-5 mr-2" />
                  Appeler maintenant
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/shop">Voir nos produits</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AlarmeHeraultPage;
